import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { CalendarCheck, CalendarPlus, Loader2, RefreshCw, Unplug } from 'lucide-react';
import {
  GoogleCalendarStatus,
  connectGoogleCalendar,
  disconnectGoogleCalendar,
  getGoogleCalendarStatus,
  syncGoogleCalendarNow,
} from '@/services/googleCalendarSync';

interface GoogleCalendarConnectProps {
  /** Recarrega a agenda depois de uma sincronização */
  onSynced?: () => void;
}

const formatSyncTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'ainda não';

export const GoogleCalendarConnect = ({ onSynced }: GoogleCalendarConnectProps) => {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<GoogleCalendarStatus | null>(null);
  const [busy, setBusy] = useState<'sync' | 'disconnect' | 'connect' | null>(null);

  const refresh = async () => setStatus(await getGoogleCalendarStatus().catch(() => null));

  useEffect(() => {
    refresh();
  }, []);

  // Retorno da tela de consentimento do Google
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('google');
    if (!result) return;
    if (result === 'conectado') {
      toast({ title: 'Google Agenda conectado!', description: 'Seus compromissos já estão sendo sincronizados.' });
      onSynced?.();
    } else if (result === 'cancelado') {
      toast({ title: 'Conexão cancelada', description: 'O Google Agenda não foi conectado.' });
    } else {
      toast({
        title: 'Não foi possível conectar o Google Agenda',
        description: params.get('detalhe') || 'Tente novamente em instantes.',
        variant: 'destructive',
      });
    }
    navigate(location.pathname, { replace: true });
    refresh();
  }, [location.search]);

  // Fora do ar no servidor (ambiente local) ou falha ao consultar: não mostra nada
  if (!status) return null;

  const handleConnect = async () => {
    if (status.error) {
      toast({ title: 'Não foi possível verificar o Google Agenda', description: `${status.error}. Saia e entre de novo no sistema e tente outra vez.`, variant: 'destructive' });
      refresh();
      return;
    }
    setBusy('connect');
    try {
      await connectGoogleCalendar();
    } catch (error: any) {
      setBusy(null);
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const handleSync = async () => {
    setBusy('sync');
    try {
      await syncGoogleCalendarNow();
      toast({ title: 'Agenda sincronizada com o Google' });
      onSynced?.();
    } catch (error: any) {
      toast({ title: 'Erro ao sincronizar', description: error.message, variant: 'destructive' });
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const handleDisconnect = async () => {
    setBusy('disconnect');
    try {
      await disconnectGoogleCalendar();
      toast({ title: 'Google Agenda desconectado', description: 'A agenda do escritório continua no seu Google, mas não sincroniza mais.' });
    } finally {
      setBusy(null);
      refresh();
    }
  };

  if (!status.connected) {
    return (
      <Button
        variant="outline"
        onClick={handleConnect}
        disabled={!status.configured || status.setupPending || busy === 'connect'}
        title={!status.configured || status.setupPending ? 'Integração ainda não configurada no servidor' : 'Conectar ao Google Agenda'}
        className="flex items-center gap-2"
      >
        {busy === 'connect' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
        Conectar Google Agenda
      </Button>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
          <CalendarCheck className="h-4 w-4" />
          Google Agenda
          {status.lastError && <span className="h-2 w-2 rounded-full bg-amber-500" title="Última sincronização com erro" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">Sincronizado com o Google</p>
          <p className="text-xs text-slate-500">{status.email}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
          Ligado à sua <strong>agenda principal do Google</strong>. O que você marcar no celular ou no tablet entra no sistema, e o que marcar aqui aparece lá.
          <p className="mt-1.5 text-slate-400">Última sincronização: {formatSyncTime(status.lastSyncedAt)}</p>
          {status.lastError && <p className="mt-1 text-amber-700">Atenção: {status.lastError}</p>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="flex-1 bg-slate-900 text-white hover:bg-slate-800" onClick={handleSync} disabled={!!busy}>
            {busy === 'sync' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
            Sincronizar agora
          </Button>
          <Button size="sm" variant="ghost" className="text-slate-500 hover:text-red-600" onClick={handleDisconnect} disabled={!!busy} title="Desconectar">
            {busy === 'disconnect' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
